/**
 * 2048 Game Persistence Service
 *
 * Auto-saves game state to local storage after each move.
 * Follows SudokuGamePersistence pattern.
 */

import { Game2048State } from '../types';
import { LocalStorageManager } from '../../services/LocalStorageManager';

export interface Saved2048Game {
  gameId: string;
  gameState: Game2048State;
  metadata: {
    boardSize: number;
    startedAt: number;
    lastSavedAt: number;
  };
}

export class Game2048GamePersistence {
  private storage: LocalStorageManager;
  private readonly AUTO_SAVE_KEY = 'game_2048_auto_save';

  constructor(storage: LocalStorageManager) {
    this.storage = storage;
  }

  /** Auto-save the current game state after each move. */
  autoSave(
    gameState: Game2048State,
    metadata: Saved2048Game['metadata']
  ): boolean {
    try {
      const savedGame: Saved2048Game = {
        gameId: 'auto_save',
        gameState,
        metadata: { ...metadata, lastSavedAt: Date.now() },
      };
      localStorage.setItem(this.AUTO_SAVE_KEY, JSON.stringify(savedGame));
      return true;
    } catch (error) {
      console.error('Failed to auto-save 2048 game:', error);
      return false;
    }
  }

  /** Load the auto-saved game state. */
  loadAutoSave(): Saved2048Game | null {
    try {
      const data = localStorage.getItem(this.AUTO_SAVE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Failed to load 2048 auto-save:', error);
      return null;
    }
  }

  /** Clear the auto-saved game state. */
  clearAutoSave(): boolean {
    try {
      localStorage.removeItem(this.AUTO_SAVE_KEY);
      return true;
    } catch (error) {
      console.error('Failed to clear 2048 auto-save:', error);
      return false;
    }
  }

  /** Check if an auto-save exists. */
  hasAutoSave(): boolean {
    return this.loadAutoSave() !== null;
  }
}
